import type { FC } from "react"
import React, { useEffect, createContext, useContext } from "react"

import type { GraphQLClient } from "graphql-request"
import { useLocation, useNavigate } from "react-router-dom"

import makeGraphqlRequestClient from "~/utils/clients/graphqlRequestClient"

import useLocalStorageState from "./useLocalStorageState"

const pass = (a: string) => a

interface GqlTools {
  client: GraphQLClient
  revokeToken: VoidFunction
}

const defaultValue: GqlTools = {
  client: makeGraphqlRequestClient(``),
  revokeToken: (): void => {
    // intentionally left blank
  },
}

export const GraphqlClientContext = createContext<GqlTools>(defaultValue)

export const useGraphqlClient = (): GqlTools => {
  const context = useContext(GraphqlClientContext)
  if (context === undefined) {
    throw new Error(
      `useGraphqlClient must be used within a GraphqlClientProvider`,
    )
  }

  return context
}

const GraphqlClientProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [storedToken, setStoredToken, clearStoredToken] =
    useLocalStorageState<string>({
      key: `jwt`,
      initialState: ``,
      serialize: pass,
      deserialize: pass,
    })

  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const newToken = new URLSearchParams(search).get(`jwt`)

  const client = makeGraphqlRequestClient(newToken || storedToken)

  useEffect(() => {
    if (newToken) {
      setStoredToken(newToken)
      client.setHeader(`authorization`, `Bearer ${newToken}`)

      const searchParams = new URLSearchParams(search)
      searchParams.delete(`jwt`)

      navigate({ pathname, search: searchParams.toString() }, { replace: true })
    }

    return () => {
      // intentionally left blank
    }
  }, [newToken, setStoredToken, client, pathname, navigate, search])

  const revokeToken = () => {
    clearStoredToken()
    client.setHeader(`authorization`, ``)
  }

  const value = { client, revokeToken }

  return (
    <GraphqlClientContext.Provider value={value}>
      {children}
    </GraphqlClientContext.Provider>
  )
}

export default GraphqlClientProvider
